"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { EmptyNewsState } from "./EmptyNewsState";
import { getImageUrl } from "@/utils/image";
import { NewsItem } from "@/types/newsType";
import { useRouter } from "next/navigation";

interface NewsCarouselProps {
  news: NewsItem[];
}

export function NewsCarousel({ news }: NewsCarouselProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const router = useRouter();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % news.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [news.length]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % news.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + news.length) % news.length);
  };

  const navigateToNewsDetail = (slug: string) => {
    router.push(`/news/${slug}`);
  };

  if (!news.length) {
    return (
      <div className="w-full h-96 md:h-[500px] bg-gray-100 rounded-lg mb-8 flex items-center justify-center">
        <EmptyNewsState
          title="Chưa có tin tức nổi bật"
          description="Hiện tại chưa có tin tức nổi bật nào. Vui lòng quay lại sau."
        />
      </div>
    );
  }

  return (
    <div className="relative w-full h-96 md:h-[500px] overflow-hidden rounded-lg shadow-lg mb-8">
      {/* Carousel Container */}
      <div className="relative w-full h-full">
        {news.map((item, index) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-500 ${
              index === currentSlide ? "opacity-100" : "opacity-0"
            }`}
          >
            {/* Background Image */}
            <div
              className="relative w-full h-full bg-cover bg-center"
              style={{
                backgroundImage: `url(${getImageUrl(item.img?.url)})`,
              }}
            />

            {/* Content Overlay */}
            <div className="absolute inset-0 flex items-end">
              <div className="w-full p-6 md:p-8 text-white">
                <div
                  className="mx-auto cursor-pointer"
                  onClick={() => navigateToNewsDetail(item.id)}
                >
                  <h2 className="text-2xl md:text-4xl font-bold mb-3 leading-tight bg-gray-900/50 p-4 rounded-lg">
                    {item.title}
                  </h2>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2 rounded-full transition-all"
          aria-label="Previous slide"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2 rounded-full transition-all"
          aria-label="Next slide"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>

        {/* Dots Indicator */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
          {news.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentSlide
                  ? "bg-white"
                  : "bg-white bg-opacity-50 hover:bg-opacity-75"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
